/** Which fixture is the visitor looking at, read from the operator's own page.
 *
 * ⛔ WHY THIS EXISTS. `DigitainSource.getMatches()` returns the match the
 * SportFrame bus reported, and the bus only ever speaks when the operator runs
 * the sportsbook in an IFRAME or forwards events to
 * `window.BidCanvasCompanion.ingest`. Parikara does neither — it is native DOM,
 * `SportFrame.elem` is null, and nothing forwards. So `current_context` has been
 * null on every single request from that operator, and the backend's
 * browsed-event boost (1.6, measured: a navigated leg is staked 2.3x higher)
 * has never once been able to fire there.
 *
 * Observed live 2026-08-31: a visitor sitting on Lecce v Roma was shown a
 * women's tennis match, because as far as the widget knew he was nowhere.
 *
 * ⛔ NO DEEP LINK TO PARSE. Parikara routes in memory — /betting stays /betting
 * when a match opens — so the URL carries no event id. The page's own text is
 * the only signal available without operator integration.
 *
 * ⭐ THE GUARD THAT MAKES THIS SAFE: we never invent an id. We read candidate
 * team names off the page and require them to match EXACTLY ONE entry in the
 * board we already hold. A wrong read resolves to nothing, which is precisely
 * today's behaviour — so this can restore the open match but can never point at
 * the wrong one.
 */

export interface BoardEntry {
  id?: string | number;
  home?: string;
  away?: string;
  home_en?: string;
  away_en?: string;
}

/** Lowercased, punctuation- and space-stripped, for tolerant comparison. */
function norm(s: string): string {
  return (s || "")
    .toLowerCase()
    .replace(/[‐-―]/g, "-")
    .replace(/[^\p{L}\p{N}]+/gu, "");
}

/** Every reasonably short text run on the page, INCLUDING inside shadow roots.
 *
 * ⛔ SHADOW-PIERCING IS THE WHOLE POINT. Parikara renders its sportsbook as a
 * `<sport-mobile>` web component with an open shadow root, so a plain
 * `document.querySelectorAll("*")` sees only the site chrome — measured live
 * 2026-08-31 on the Lecce v Roma page: 627 elements and NO team names without
 * piercing, 1877 elements and both names with it. The first version of this
 * file did not pierce, found nothing, and shipped a fix that could never work.
 *
 * Deliberately NOT a TreeWalker: it returns nothing against a DOMParser
 * document in the test environment, so the traversal would have been the one
 * part of this file no test could cover.
 */
function collectTexts(root: Document | ShadowRoot, depth: number, out: string[]): void {
  // Depth 6 covers a component tree nested well past anything observed, and
  // bounds a pathological page.
  if (depth > 6 || out.length > 4000) return;
  let els: NodeListOf<Element>;
  try {
    els = root.querySelectorAll("*");
  } catch {
    return;
  }
  const limit = Math.min(els.length, 4000);
  for (let i = 0; i < limit; i++) {
    const el = els[i];
    const kids = el.childNodes;
    for (let j = 0; j < kids.length; j++) {
      const n = kids[j];
      if (n.nodeType !== 3) continue; // text nodes only
      const t = (n.textContent || "").trim();
      if (t.length >= 2 && t.length <= 40) out.push(t);
    }
    const sr = (el as Element & { shadowRoot?: ShadowRoot | null }).shadowRoot;
    if (sr) collectTexts(sr, depth + 1, out);
  }
}

function pageTexts(doc: Document): string[] {
  const out: string[] = [];
  collectTexts(doc, 0, out);
  return out;
}

/**
 * The board entry whose BOTH team names appear on the page, when exactly one
 * does. Returns null on zero matches and on ambiguity — never a guess.
 *
 * The localized names (`home`/`away`) are what the page renders, so they are
 * tried first; the English pair is a fallback for an operator that renders
 * English. Both sides are normalised, so «Рома» matches "Рома" regardless of
 * surrounding punctuation or casing.
 */
export function detectOpenMatch(board: BoardEntry[], doc?: Document): string | null {
  const d = doc || (typeof document !== "undefined" ? document : null);
  if (!d || !d.body || !board || board.length === 0) return null;

  const texts = new Set(pageTexts(d).map(norm));
  if (texts.size === 0) return null;

  const hits: string[] = [];
  for (const m of board) {
    const id = String(m.id ?? "").trim();
    if (!id) continue;
    const pairs: Array<[string, string]> = [
      [norm(m.home || ""), norm(m.away || "")],
      [norm(m.home_en || ""), norm(m.away_en || "")],
    ];
    for (const [h, a] of pairs) {
      // Both names, both non-trivial. A one-letter "team" would match anything.
      if (h.length >= 3 && a.length >= 3 && texts.has(h) && texts.has(a)) {
        hits.push(id);
        break;
      }
    }
  }
  // ⛔ Exactly one. A board page listing many fixtures matches many entries, and
  // that is NOT someone looking at a match — it is the lobby, where the pool is
  // the right answer.
  const uniq = [...new Set(hits)];
  return uniq.length === 1 ? uniq[0] : null;
}
